import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Download, LogOut, RefreshCw, Inbox, ClipboardPen, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/constants/site";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const SOURCE_STYLES = {
  website: "bg-brand-sky/15 text-brand-deepsky",
  "Walk-in": "bg-brand-sun/25 text-amber-700",
  "Phone Call": "bg-emerald-100 text-emerald-700",
  Referral: "bg-brand-coral/15 text-brand-deepcoral",
  Other: "bg-slate-100 text-slate-600",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const navigate = useNavigate();
  const token = sessionStorage.getItem("bca_admin_token");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data);
    } catch {
      sessionStorage.removeItem("bca_admin_token");
      navigate("/admin");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    load();
  }, [token, navigate, load]);

  const exportCsv = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/leads/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "blooming-chaitanya-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed. Please login again.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("bca_admin_token");
    navigate("/admin");
  };

  return (
    <main data-testid="admin-leads-page" className="mx-auto max-w-7xl px-6 pb-24 pt-36">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-deepsky mb-2">Admin</p>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">Admission Enquiries</h1>
          <p className="mt-2 text-sm text-slate-500" data-testid="leads-count">
            {leads ? `${leads.length} enquiry${leads.length === 1 ? "" : "ies"} · newest first` : "Loading..."}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/admin/desk"
            data-testid="leads-desk-btn"
            className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-brand-coral px-5 font-bold text-white shadow-lg transition-transform hover:scale-105"
          >
            <ClipboardPen className="h-4 w-4" /> Log Walk-in
          </Link>
          <Link
            to="/admin/news"
            data-testid="leads-news-btn"
            className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-brand-deepsky px-5 font-bold text-white shadow-lg transition-transform hover:scale-105"
          >
            <Newspaper className="h-4 w-4" /> News Posts
          </Link>
          <button
            onClick={load}
            data-testid="leads-refresh-btn"
            className="inline-flex min-h-[48px] items-center gap-2 rounded-full border-2 border-brand-sky px-5 font-bold text-brand-deepsky transition-colors hover:bg-brand-sky hover:text-white"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button
            onClick={exportCsv}
            data-testid="leads-export-btn"
            className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-brand-sun px-5 font-bold text-ink shadow-lg transition-transform hover:scale-105"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex min-h-[48px] items-center gap-2 rounded-full border-2 border-slate-300 px-5 font-bold text-slate-600 transition-colors hover:bg-slate-100"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-10 overflow-x-auto rounded-[2rem] border border-amber-200/50 bg-white shadow-[0_20px_50px_rgba(2,132,199,0.08)]">
        {leads && leads.length === 0 ? (
          <div data-testid="leads-empty" className="flex flex-col items-center gap-3 py-20 text-slate-400">
            <Inbox className="h-12 w-12" />
            <p className="font-semibold">No enquiries yet — they'll appear here as parents submit the form.</p>
          </div>
        ) : (
          <Table data-testid="leads-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Parent</TableHead>
                <TableHead>Child</TableHead>
                <TableHead>Age</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>Fee Est.</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(leads || []).map((l) => (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">
                    {new Date(l.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap ${SOURCE_STYLES[l.source] || SOURCE_STYLES.Other}`}>
                      {l.source === "website" ? "Website" : l.source}
                    </span>
                  </TableCell>
                  <TableCell className="font-semibold">
                    {l.parent_name}
                    {l.email && <span className="block text-xs font-normal text-slate-400">{l.email}</span>}
                  </TableCell>
                  <TableCell>{l.child_name}</TableCell>
                  <TableCell className="whitespace-nowrap">{l.child_age}</TableCell>
                  <TableCell>
                    <a href={`tel:${l.phone}`} className="font-semibold text-brand-deepsky hover:underline whitespace-nowrap">{l.phone}</a>
                  </TableCell>
                  <TableCell>
                    <span className="inline-block rounded-full bg-brand-sky/15 px-3 py-1 text-xs font-bold text-brand-deepsky whitespace-nowrap">{l.program}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm font-semibold text-amber-700">{l.fee_estimate || "—"}</TableCell>
                  <TableCell className="max-w-[220px] truncate text-sm text-slate-500">{l.message || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </main>
  );
}
